// apps/prompts/src/hooks/useOnboarding.ts
import { useState, useCallback, useEffect } from 'react'

const STORAGE_KEY = 'neuroforge_onboarding_completed'

export interface OnboardingStep {
  id: string
  target: string // CSS selector for the element to highlight
  title: string
  description: string
  position: 'top' | 'bottom' | 'left' | 'right'
}

const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 'sidebar-nav',
    target: '[data-onboarding="sidebar-nav"]',
    title: 'Navigation rapide',
    description: 'Accède à tes favoris, collections et profil depuis la sidebar.',
    position: 'right',
  },
  {
    id: 'search',
    target: '[data-onboarding="search"]',
    title: 'Recherche instantanée',
    description:
      'Tape un mot-clé pour trouver le bon prompt en quelques secondes. Utilise ⌘+K pour ouvrir.',
    position: 'bottom',
  },
  {
    id: 'filters',
    target: '[data-onboarding="filters"]',
    title: 'Filtres intelligents',
    description:
      'Filtre par catégorie, modèle IA, ou utilise les presets pour des recherches guidées.',
    position: 'right',
  },
  {
    id: 'copy',
    target: '[data-onboarding="copy-button"]',
    title: 'Copie en 1 clic',
    description: "Chaque prompt est prêt à l'emploi. Copie-le avec les variables substituées.",
    position: 'left',
  },
]

export function useOnboarding() {
  const [isActive, setIsActive] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [completed, setCompleted] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true'
    } catch {
      return false
    }
  })

  // Auto-start on first visit (after a delay for page load)
  useEffect(() => {
    if (!completed) {
      const timer = setTimeout(() => setIsActive(true), 1500)
      return () => clearTimeout(timer)
    }
  }, [completed])

  const finish = useCallback(() => {
    setIsActive(false)
    setCompleted(true)
    setCurrentStep(0)
    try {
      localStorage.setItem(STORAGE_KEY, 'true')
    } catch {
      // Silently fail if localStorage is unavailable
    }
  }, [])

  const next = useCallback(() => {
    if (currentStep < ONBOARDING_STEPS.length - 1) {
      setCurrentStep((s) => s + 1)
    } else {
      finish()
    }
  }, [currentStep, finish])

  const previous = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1)
    }
  }, [currentStep])

  const skip = useCallback(() => {
    finish()
  }, [finish])

  const restart = useCallback(() => {
    setCompleted(false)
    setCurrentStep(0)
    setIsActive(true)
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch {
      // noop
    }
  }, [])

  return {
    isActive,
    currentStep,
    steps: ONBOARDING_STEPS,
    step: ONBOARDING_STEPS[currentStep] ?? null,
    totalSteps: ONBOARDING_STEPS.length,
    completed,
    next,
    previous,
    skip,
    restart,
  }
}
